// Safe, high-level knobs for Ziarcadia feel/layout changes.
// Add tweakable values here instead of scattering new magic numbers around the frontend.
// When a visual rule spans modes, put the shared family here first, then add mode-specific overrides only where needed.

import { PICTIONARY_TRANSLATION_SUPPORT } from "../../shared/pictionaryTranslations";
import { VAULT_TRANSLATION_SUPPORT } from "../../shared/vaultTranslations";

export const SITE_TUNING = {
    startup: { localeTimeoutMs: 2000 },
    activityHistory: { dayMinHeightPx: 86, phoneDayMinHeightPx: 76, sourceMaxHeightPx: 290, stripHeightPx: 10, stripGapPx: 2, slowLoadMs: 500 },
    musicClass: { pollMs: 2000, backgroundPollMs: 8000, maxPollMs: 10000, noticeMs: 3200, playerPollMs: 350, skipSeconds: 10, playbackShareMs: 5000, playbackDriftSeconds: 1.5, playbackStaleMs: 15000, followTop: 0.15, followBottom: 0.75, followTarget: 0.35, lastLineLoopSeconds: 8, lyricsWidthRatio: 1.2, searchPanelWidthPx: 410, searchPanelTopPx: 76,
        rosterColumnMinPx: 115, rosterRowsPerColumn: 18, rosterMaxWidthPx: 540, rosterBottomGapPx: 18 },
    vocabularyReview: {
        minWordSizePx: 26,
        maxWordSizePx: 72,
        wordViewportWidth: 3.3,
        minRowPaddingPx: 18,
        maxRowPaddingPx: 36
    },
    siteControls: {
        actionFontSizeRem: 1.15625,
        actionMinHeightPx: 54,
        compactFontSizeRem: 1,
        landingStackWidthPx: 520,
        landingHeaderWidthPx: 760,
        landingBrandSizePx: 58
    },
    autotranslator: {
        liveTranslateDelayMs: 160,
        liveTranslateMaxWaitMs: 350,
        finalTranslateDelayMs: 60,
        audioReadyTimeoutMs: 3000,
        backgroundGraceMs: 30_000,
        initialListenFeedbackMs: 3000,
        speechGlowHoldMs: 1600,
        speechGlowCycleMs: 1800,
        speechGlowFadeMs: 320
    },
    planetHoppersPrivacy: { afterSelectionMs: 4000, afterUnhideMs: 8000 },
    planetHoppersMotion: { spaceExitMs: 450, surfaceEnterMs: 520, fleetEmergeMs: 900, fleetStaggerMs: 65 },
    planetHoppersAudio: {
        musicVolume: 0.4,
        crossfadeSeconds: 4,
        stopFadeMs: 3000,
        transitionRate: 1.05,
        transitionTailMs: 1400,
        transitionPreloadTimeoutMs: 2000,
        revealRate: 0.9,
        revealGain: 2.25
    },
    planetHoppersTypography: {
        question: "clamp(40px, 4.5vw, 76px)",
        answer: "clamp(32px, 4vw, 64px)",
        mobileQuestionPx: 30,
        mobileAnswerPx: 28,
        answerFitFloorPx: 18
    },
    // Large classroom panels can occasionally arrive with laptop-style Windows scaling.
    // Keep this signature narrow so normal HiDPI laptops retain their accessibility scale.
    classroomDisplayTypography: {
        hostLobbyWidthPx: 1920,
        targetDevicePixelRatio: 1.5,
        minDevicePixelRatio: 2.75,
        maxDevicePixelRatio: 3.25,
        minViewportWidthPx: 1100,
        maxViewportWidthPx: 1500,
        minViewportHeightPx: 560,
        maxViewportHeightPx: 820,
        minPhysicalWidthPx: 3500,
        maxPhysicalWidthPx: 4200,
        minPhysicalHeightPx: 1900,
        maxPhysicalHeightPx: 2400
    },

    // Room sync guardrails. Keep these modest because every waiting-room device uses them.
    room: {
        lobbySummaryPollMs: 1200,
        lobbySummaryTimeoutMs: 8_000,
        connectionHeartbeatIntervalMs: 8_000,
        connectionHeartbeatTimeoutMs: 22_000,
        reconnectBaseDelayMs: 500,
        reconnectMaxDelayMs: 10_000,
        reconnectJitterRatio: 0.35,
        renamePromptFeedbackTimeoutMs: 8_000,
        // Let brief startup variance pass unnoticed; genuinely slow starts get a gentle full-screen reveal.
        hostStartLoadingOverlayDelayMs: 700,
        hostStartLoadingOverlayRevealMs: 500,
        audio: {
            playerJoinVolume: 2.0,
            hostControllerFoundVolume: 0.51,
            hostControllerLostVolume: 0.34,
            gameModeStartVolume: 1.15,
            firstPlaceWinnerVolume: 0.82,
            secondPlaceWinnerVolume: 0.95,
            thirdPlaceWinnerVolume: 0.9,
            globalVolumeMultiplier: 0.84,
            mobileVolumeMultiplier: 0.78,
            mobileVolumeShortSideMaxPx: 640
        }
    },

    // Host mode selection cards shown for the lobby game choices.
    hostMode: {
        cardEmblemSizePx: 56,
        cardEmblemPaddingPx: 9
    },

    // Desktop host setup split. The QR track stays independently capped, so widening
    // this rail gives the player list about 10% more room and takes it from the editor.
    hostLobby: {
        sideRailMinPx: 670,
        sideRailWidthVw: 46,
        sideRailMaxPx: 904,
        // Preserve the source editor's lead on wide screens after shell gutters.
        sideRailMaxSharePercent: 48,
        shortSideRailMinPx: 607,
        shortSideRailWidthVw: 42,
        shortSideRailMaxPx: 760
    },

    // QR sizing and rendering that shows up across host/player/controller surfaces.
    qr: {
        bitmapMarginModules: 1,
        bitmapWidthPx: 640,
        bitmapDarkColorHex: "#0f1417",
        bitmapLightColorHex: "#ffffff",
        frameMaxWidthPx: 320,
        frameMaxWidthMobilePx: 280,
        hostLobbyFrameMaxWidthPx: 354,
        framePaddingPx: 16,
        frameRadiusPx: 28,
        compactFramePaddingPx: 10,
        compactFramePaddingSmallPx: 8,
        hostMobileFrameWidthPx: 92,
        hostMobileFrameWidthSmallPx: 80,
        hostMobileFrameRadiusPx: 20,
        hostMobileFrameRadiusSmallPx: 18,
        rosterOverlayFrameWidthPx: 64,
        rosterOverlayFrameWidthSmallPx: 52,
        rosterOverlayFramePaddingPx: 4,
        rosterOverlayFramePaddingSmallPx: 3,
        rosterOverlayFrameRadiusPx: 13,
        rosterOverlayFrameRadiusSmallPx: 10,
        rosterOverlayOpacity: 0.62,
        activeRoomFrameWidthPx: 130,
        activeRoomFrameWidthMobilePx: 120,
        activeRoomFrameWidthSmallPx: 106,
        // Keep the always-visible in-game join code scannable from across a classroom.
        activeRoomHostFrameWidthPx: 194,
        activeRoomHostFrameWidthMobilePx: 180,
        activeRoomHostFrameWidthSmallPx: 122,
        activeRoomHostCompactFrameWidthPx: 107,
        activeRoomHostShortFrameWidthPx: 86,
        activeRoomHostStackedFrameWidthPx: 115,
        activeRoomFrameRadiusPx: 18,
        activeRoomFrameRadiusSmallPx: 16,
        controllerInlineWidthPx: 144,
        controllerInlineWidthTabletPx: 140,
        controllerInlineWidthMobilePx: 132,
        controllerFrameRadiusPx: 18,
        previewPanelMaxWidthPx: 900,
        previewPanelMaxWidthMobilePx: 720,
        previewMaxWidthPx: 576,
        previewMaxWidthMobilePx: 448,
        previewMaxHeightSvh: 56,
        previewMaxHeightMobileSvh: 40,
        previewFrameRadiusPx: 34,
        previewCodeFontSizeRem: 8,
        previewCodeFontSizeMobileRem: 4.5
    },

    // Relic Run board size plus movement feel. These affect presentation, not gameplay rules.
    relicRun: {
        board: {
            cellSizePx: 92,
            tileSizePx: 78,
            overlayTreasureSizePx: 92,
            mobileBoardScale: 0.88,
            desktopBoardScale: 1,
            desktopAvatarSizePx: 58,
            desktopAvatarRadiusPx: 18,
            desktopAvatarBorderPx: 5,
            desktopAvatarHaloPx: 11,
            maxCanvasDevicePixelRatio: 2,
            // Soft decorative particles do not need the word layer's HiDPI backing.
            maxAccentCanvasDevicePixelRatio: 1,
            // Large maze worlds can otherwise allocate two multi-megapixel canvas layers.
            // Scale DPR down only as much as needed to keep mobile redraws responsive.
            maxCanvasBackingPixels: 4_200_000,
            maxTileBitmapCachePixels: 8_000_000
        },
        layout: {
            mobileHostStageCardMinHeightSvh: 63,
            mobileHostStageCardMaxHeightPx: 560,
            mobileHostStageMinHeightSvh: 56,
            mobileHostStageMaxHeightPx: 500
        },
        playerMotion: {
            cameraPanEasing: "cubic-bezier(0.14, 0.84, 0.18, 1)",
            hopArcEasing: "cubic-bezier(0.18, 0.78, 0.18, 1)",
            cameraPanDurationMs: 180,
            hopArcDurationMs: 140,
            optimisticSyncTimeoutMs: 1200,
            // Require the hop to be underway before a follow-up tap can queue the next move.
            bufferedMoveLeadInMs: 48,
            maxTracedMoveQueue: 4,
            tracedCameraStepDistanceCells: 0.7,
            touchSwipeMinDistancePx: 34,
            touchSwipeAxisDominanceRatio: 1.08,
            touchSwipeClickSuppressMs: 450
        },
        camera: {
            edgeMarginCells: 1.8,
            startInsetCells: 1.3,
            courseInsetCells: 0.9,
            leftEdgeMarginCells: 1.1,
            minDeadzoneCells: 0.7,
            viewportCells: 5.4,
            minBoardScale: 0.58
        },
        reveal: {
            outerDiagonalOpacity: 0.58,
            unrevealedTileOpacity: 0.28
        },
        moveTrail: {
            // Delay before the gold streak appears after a successful move starts.
            revealDelayMs: 18,
            desktopRevealDurationMs: 430,
            mobileRevealDurationMs: 520,
            desktopLengthScale: 0.88,
            mobileLengthScale: 0.94,
            desktopThicknessPx: 12,
            mobileThicknessPx: 14
        },
        wrongTile: {
            settleMaxMs: 40,
            settleWithoutHopMs: 24,
            fallDirectionRandomnessRadians: 0.24,
            sideFallAngleOffsetRadians: 0.18,
            playerFallDistanceBasePx: 30,
            playerFallDistanceRandomPx: 14,
            collapsingTileExtraDistanceBasePx: 8,
            collapsingTileExtraDistanceRandomPx: 12,
            collapsingTileFallDurationMs: 300,
            playerFallDurationMs: 780,
            flareDelayAfterFallMs: 650,
            removeCollapsedTileDelayMs: 24,
            cameraReturnDelayAfterFlareMs: 260,
            respawnDelayAfterCameraMs: 420
        },
        portalTransition: {
            fallDestinationYOffsetPx: 46,
            fallDistanceBasePx: 18,
            fallDistanceRandomPx: 10,
            respawnDurationMs: 440,
            entryFallDurationMs: 560,
            entryTextDelayMs: 0,
            portalFallDurationMs: 760,
            blackoutFadeInMs: 260,
            flareDelayAfterSettleMs: 32,
            fallDelayAfterSettleMs: 56,
            blackoutDelayAfterFallMs: 40,
            boardExitDelayAfterBlackoutMs: 28,
            boardExitDurationMs: 240,
            boardSwapDelayAfterExitMs: 72,
            boardEnterDelayAfterSwapMs: 48,
            boardEnterDurationMs: 220,
            entryDelayAfterBoardEnterMs: 20,
            curtainFadeOutMs: 240
        },
        passagePreview: {
            maxLines: 3,
            initialVisibleTokenCount: 20,
            replayContextBeforeTokens: 7,
            replayContextAfterTokens: 9
        },
        theme: {
            levelTints: [
                "204 94 72",
                "82 124 226",
                "138 96 220",
                "86 172 112",
                "216 132 52",
                "196 84 150"
            ],
            stageWashAlpha: 0.1,
            stageGridAlpha: 0.08,
            tileWashAlpha: 0.28,
            canvasTileTintMix: 0.075,
            canvasTileShineAlpha: 0.14
        }
    },

    // Random Teams presentation and motion knobs.
    randomTeams: {
        layout: {
            memberTokenSizePx: 76,
            presentationMemberTokenSizePx: 94,
            namedMemberTokenWidthPx: 208,
            namedPreviewTokenWidthPx: 48,
            namedFacialFeatureStretch: 1.04,
            namedMemberTokenHeightPx: 104,
            namedMemberTokenWidthMobilePx: 176,
            namedMemberTokenHeightMobilePx: 96,
            namedMemberFontSizeCqw: 13,
            desktopTeamPresentationMaxScale: 1.18,
            orderTokenSlotWidthPx: 92,
            orderRowColumnGapPx: 16,
            orderRowColumnGapMobilePx: 8,
            orderRowSidePaddingPx: 28,
            orderMembersInlinePaddingPx: 16,
            orderConnectorLaneTokenReserve: 0.72,
            minOrderTokensPerRow: 1,
            maxOrderTokensPerRow: 32,
            orderFinishedLaneMinWidthPx: 196,
            orderFinishedLaneWidthPx: 196,
            orderFinishedLaneGapPx: 18,
            stageResultControlGutterPx: 48,
            stageOrderResultControlGutterPx: 72,
            mobileKeyboardMinInsetPx: 80
        },
        animation: {
            maxAnimatedLaunchMemberCount: 100,
            launchTimeScale: 1,
            launchPixelsPerMs: 2.1,
            minLaunchDurationMs: 500,
            maxLaunchDurationMs: 960,
            sideBounceLaunchPixelsPerMs: 1.9,
            sideBounceMinLaunchDurationMs: 460,
            sideBounceMaxLaunchDurationMs: 900,
            shortEntryDistanceThresholdPx: 360,
            shortEntryMinLaunchDurationMs: 420,
            launchStaggerMs: 64,
            sideBounceLaunchStaggerMs: 70,
            maxLaunchStaggerWindowMs: 1600,
            tallStageSideBounceRatio: 0.9,
            flightCurveStrength: 0.6,
            floorArcHeightTokens: 0.48,
            trayArcHeightTokens: 0.3,
            sideArcMinHeightPx: 18,
            sideArcMaxHeightPx: 48,
            sideMaxContactOffset: 0.84,
            floorHorizontalRetention: 0.82,
            wallHorizontalRetention: 0.78,
            velocityTiltRatio: 0.2,
            flightSampleCount: 48,
            contactCompressionMs: 26,
            landingSettleMs: 72,
            audioSyncLeadMs: 34,
            manualDropAudioSyncLeadMs: 82,
            staleAudioDelayMs: 180
        },
        audio: {
            bounceVolume: 0.18,
            latchVolume: 0.22,
            globalVolumeMultiplier: 0.8,
            mobileVolumeMultiplier: 0.5,
            mobileVolumeShortSideMaxPx: 640
        }
    },

    // Waiting-room movement feel and sync tolerance.
    lobby: {
        layout: {
            hostDesktopRosterMaxWidthPx: 520,
            hostDesktopQrCardMaxWidthPx: 406,
            hostRosterListMaxViewportHeightSvh: 48,
            hostRosterListMaxHeightPx: 520,
            hostPanelSurfaceMixPercent: 60,
            hostPanelActiveSurfaceMixPercent: 90
        },
        camera: {
            // Joiners should feel like they are moving inside the room, not dragging the whole map.
            playerViewportScale: 2.35,
            playerViewportScaleMobile: 3.2,
            // Let the player drift a touch before the camera gives way.
            followDeadzoneWidthRatio: 0.24,
            followDeadzoneHeightRatio: 0.2
        },
        movement: {
            playerSpeedPxPerSecond: 310,
            pointerSnapThresholdPx: 14,
            moveSyncIntervalMs: 28,
            positionSyncEpsilon: 0.001,
            localPositionHardSnapDistance: 0.34,
            localPositionEmergencySnapDistance: 0.56,
            hardSnapIdleMs: 820,
            serverSettleGraceMs: 220,
            serverSettleEpsilonPx: 2,
            serverCorrectionSpeedPxPerSecond: 560,
            remoteSmoothingMs: 95,
            remoteHardSnapDistance: 0.18,
            pelletSplashPositionDelta: 0.01,
            spectatorRenderIntervalMs: 40,
            spectatorIdleRenderIntervalMs: 320
        },
        feedback: {
            // A short destination ripple makes tap-to-move legible without leaving a persistent objective behind.
            moveTargetDurationMs: 480,
            pelletExpressionDurationMs: 900,
            highFiveCooldownMs: 10_000,
            highFiveTouchDistancePx: 42,
            highFiveEffectDurationMs: 720,
            highFiveSpeedBoostDurationMs: 2_800,
            highFiveSpeedBoostMultiplier: 1.6,
            highFiveLaunchDurationMs: 2_800,
            highFiveLaunchSteeringMultiplier: 0.12,
            highFiveLaunchSpeedPxPerSecond: 720,
            highFiveDirectionMemoryMs: 200
        },
        bounds: {
            minX: 0.04,
            maxX: 0.96,
            minY: 0.06,
            maxY: 0.94
        }
    },

    // Shared player roster density for waiting rooms and active game lists.
    // Count badges are visual overlays by design; do not reserve layout rows or columns for them.
    playerRoster: {
        roomyMaxCount: 8,
        balancedMaxCount: 20,
        compactMaxCount: 30,
        roomyColumnMinPx: 220,
        balancedColumnMinPx: 156,
        compactColumnMinPx: 144,
        denseColumnMinPx: 88,
        roomyRowMinPx: 66,
        balancedRowMinPx: 58,
        compactRowMinPx: 36,
        denseRowMinPx: 28,
        roomyGapPx: 12,
        balancedGapPx: 10,
        compactGapPx: 5,
        denseGapPx: 4,
        roomyRowPaddingYPx: 12,
        balancedRowPaddingYPx: 10,
        compactRowPaddingYPx: 4,
        denseRowPaddingYPx: 3,
        roomyRowPaddingXPx: 12,
        balancedRowPaddingXPx: 11,
        compactRowPaddingXPx: 7,
        denseRowPaddingXPx: 6,
        roomyActionSizePx: 30,
        balancedActionSizePx: 28,
        compactActionSizePx: 22,
        denseActionSizePx: 20,
        roomyBadgeMinHeightPx: 26,
        balancedBadgeMinHeightPx: 24,
        compactBadgeMinHeightPx: 20,
        denseBadgeMinHeightPx: 18,
        roomyNameFontSizeRem: 1.1,
        singleRoomyNameFontSizeRem: 1.22,
        balancedNameFontSizeRem: 0.92,
        compactNameFontSizeRem: 0.8,
        denseNameFontSizeRem: 0.76,
        roomyBadgeFontSizeRem: 0.66,
        balancedBadgeFontSizeRem: 0.62,
        compactBadgeFontSizeRem: 0.6,
        denseBadgeFontSizeRem: 0.58,
        floatingCountSizePx: 30,
        floatingCountGutterPx: 0
    },

    vaultBreakers: {
        roster: { nameMinFontSizeRem: 0.7 },
        typography: { categoryMinPx: 20, categoryMaxPx: 28, categoryCompactMinPx: 14, categoryMaxLines: 3, categoryZoomScale: 2 },
        translations: { ...VAULT_TRANSLATION_SUPPORT, leaderDurationFraction: 0.5 },
        answerPrivacy: { reviewMs: 2400, fadeMs: 1400 },
        banknote: { sheenMs: 5400, maxWidthPx: 820 },
        motion: {
            // Emergency kill switch for all Vault Breakers motion/transitions.
            enabled: true,
            surfaceSettleMs: 520,
            surfaceExitMs: 140,
            surfaceExitScale: 0.985,
            surfaceStartOpacity: 0.4,
            surfaceStartScale: 0.975,
            vaultEntryLeapMs: 420,
            vaultEntryZoomMs: 380,
            vaultEntryZoomScale: 3.2,
            vaultEntryZoomMaxScale: 6,
            vaultReturnZoomMs: 340,
            vaultReturnFadeMs: 180,
            surfaceEasing: "cubic-bezier(0.32, 0, 0.28, 1)",
            crackHandoffMs: 1200,
            stashIncreaseMs: 1800,
            slowSurfacePreparationMs: 80,
            scannerDiscoveryPulseMs: 1200,
            scannerReadyPulseMs: 2200,
            finalVaultPulseMs: 4200,
            firstRollGlowMs: 2600,
            scannerTraceLoopMs: 1800
        },
        mascot: {
            sizePx: 38.4,
            compactSizePx: 28.8,
            boardVaultInsetPx: 3,
            playerWaitingSizePx: 92,
            playerWaitingCompactSizePx: 76,
            playerWaitingIdleMs: 4200,
            runPxPerSecond: 500,
            climbPxPerSecond: 220,
            dropPxPerSecond: 250,
            entryReachSizeRatio: 2.5,
            entryLeapHeightPx: 40,
            hoverIntentMs: 70,
            minSegmentMs: 80,
            landingMs: 260,
            sparkleTrailIntervalMs: 42,
            sparkleTrailLifetimeMs: 850
        },
        audio: {
            hostVaultSelectedVolume: 0.9,
            finalVaultVolume: 0.7,
            dailyDoubleVolume: 0.52,
            diamondFoundVolume: 1.0,
            stashFoundVolume: 0.7,
            vaultCrackedVolume: 0.47,
            failedToCrackVolume: 0.4,
            wordSelectedVolume: 2.25,
            vaultRunThemeVolume: 0.44,
            jeopardyThemeVolume: 0.89,
            // Keep the waveform-aligned rate, but lead the attack by another half second
            // so the Massive Loot cue lands before the reveal settles.
            jeopardyThemeStartOffsetSeconds: 0.5,
            jeopardyThemePlaybackRate: 1.013,
            finalLockFadeMs: 520,
            globalVolumeMultiplier: 0.84,
            mobileVolumeMultiplier: 0.78,
            mobileVolumeShortSideMaxPx: 640
        }
    },

    // Small pictionary feel controls that are safe to tweak.
    pictionary: {
        motion: { surfaceEnterMs: 480, clueEnterMs: 620 },
        translations: {
            ...PICTIONARY_TRANSLATION_SUPPORT,
            discoveryPulseMs: 1400
        },
        layout: {
            caseFolderTopCornerPx: 5,
            caseFolderBottomCornerPx: 18,
            caseFileLabelOffsetXPx: -4,
            caseFileLabelOffsetYPx: -1,
            mobileKeyboardMinInsetPx: 80,
            keyboardLiftMaxInsetPx: 56,
            keyboardLiftInsetRatio: 0.16,
            keyboardScrollRestoreDelayMs: 140,
            shortKeyboardViewportMaxHeightPx: 520
        },
        drawing: {
            brushWidthPx: 6,
            // Ignore sub-pixel sensor chatter well below the visible brush width, while
            // still accumulating slow movement from the last accepted point.
            minPointDistancePx: 0.6,
            maxPointGapPx: 8,
            maxStrokeJumpPx: 118,
            smoothingWeight: 0.18,
            safeBottomInsetMinPx: 104,
            safeBottomInsetMaxPx: 188,
            safeBottomInsetRatio: 0.22
        },
        audio: {
            rightAnswerVolume: 0.63,
            wordStartVolume: 0.84,
            wordFinishedVolume: 0.51,
            countdownTickVolume: 0.24,
            countdownTockVolume: 0.24,
            thinkingMusicVolume: 0.16,
            globalVolumeMultiplier: 0.84,
            mobileVolumeMultiplier: 0.78,
            mobileVolumeShortSideMaxPx: 640
        },
        idle: {
            particleCount: 28,
            roundCompletePromptDelayMs: 20000,
            roundCompletePromptFadeMs: 13000
        },
        mascot: {
            initialPeekDelayMs: 3500,
            roundPeekDelayMs: 10000,
            guardRetryMs: 900,
            visibleMs: 4800,
            retreatMs: 340,
            tapReactionMs: 520,
            tapRelocateDelayMs: 1200,
            cooldownMs: 24000,
            maxAppearancesPerContext: 2,
            sizePx: 34,
            compactSizePx: 27
        },
        feedback: {
            celebrationDurationMs: 5200
        }
    }
} as const;

const px = (value: number): string => `${value}px`;
const rem = (value: number): string => `${value}rem`;
const vw = (value: number): string => `${value}vw`;
const supportsSmallViewportHeightUnit = (): boolean =>
    typeof CSS !== "undefined" && typeof CSS.supports === "function" && CSS.supports("height", "100svh");

const svh = (value: number): string => `${value}${supportsSmallViewportHeightUnit() ? "svh" : "vh"}`;

export const applySiteTuningCssVariables = (root: HTMLElement = document.documentElement) => {
    const vaultMotionEnabled = SITE_TUNING.vaultBreakers.motion.enabled;
    const variables: Record<string, string> = {
        "--music-lyrics-width-ratio": `${SITE_TUNING.musicClass.lyricsWidthRatio}fr`,
        "--music-search-panel-width": px(SITE_TUNING.musicClass.searchPanelWidthPx),
        "--music-search-panel-top": px(SITE_TUNING.musicClass.searchPanelTopPx),
        "--activity-day-min-height": px(SITE_TUNING.activityHistory.dayMinHeightPx),
        "--activity-phone-day-min-height": px(SITE_TUNING.activityHistory.phoneDayMinHeightPx),
        "--activity-source-max-height": px(SITE_TUNING.activityHistory.sourceMaxHeightPx),
        "--activity-strip-height": px(SITE_TUNING.activityHistory.stripHeightPx),
        "--activity-strip-gap": px(SITE_TUNING.activityHistory.stripGapPx),
        "--vocabulary-review-word-size": `clamp(${SITE_TUNING.vocabularyReview.minWordSizePx}px, ${SITE_TUNING.vocabularyReview.wordViewportWidth}vw, ${SITE_TUNING.vocabularyReview.maxWordSizePx}px)`,
        "--vocabulary-review-row-padding": `clamp(${SITE_TUNING.vocabularyReview.minRowPaddingPx}px, 2.2vh, ${SITE_TUNING.vocabularyReview.maxRowPaddingPx}px)`,
        "--ziar-action-font-size": `${SITE_TUNING.siteControls.actionFontSizeRem}rem`,
        "--ziar-action-min-height": px(SITE_TUNING.siteControls.actionMinHeightPx),
        "--ziar-compact-font-size": `${SITE_TUNING.siteControls.compactFontSizeRem}rem`,
        "--ziar-landing-stack-width": px(SITE_TUNING.siteControls.landingStackWidthPx),
        "--ziar-landing-header-width": px(SITE_TUNING.siteControls.landingHeaderWidthPx),
        "--ziar-landing-brand-size": px(SITE_TUNING.siteControls.landingBrandSizePx),
        "--pictionary-decode-discovery-ms": `${SITE_TUNING.pictionary.translations.discoveryPulseMs}ms`,
        "--pictionary-folder-top-corner": px(SITE_TUNING.pictionary.layout.caseFolderTopCornerPx),
        "--pictionary-folder-bottom-corner": px(SITE_TUNING.pictionary.layout.caseFolderBottomCornerPx),
        "--pictionary-case-file-label-offset-x": px(SITE_TUNING.pictionary.layout.caseFileLabelOffsetXPx),
        "--pictionary-case-file-label-offset-y": px(SITE_TUNING.pictionary.layout.caseFileLabelOffsetYPx),
        "--ph-question-font": SITE_TUNING.planetHoppersTypography.question,
        "--ph-answer-font": SITE_TUNING.planetHoppersTypography.answer,
        "--ph-mobile-question-font": px(SITE_TUNING.planetHoppersTypography.mobileQuestionPx),
        "--ph-mobile-answer-font": px(SITE_TUNING.planetHoppersTypography.mobileAnswerPx),
        "--ziar-qr-frame-max-width": px(SITE_TUNING.qr.frameMaxWidthPx),
        "--ziar-qr-frame-max-width-mobile": px(SITE_TUNING.qr.frameMaxWidthMobilePx),
        "--ziar-qr-host-lobby-frame-max-width": px(SITE_TUNING.qr.hostLobbyFrameMaxWidthPx),
        "--ziar-qr-frame-padding": px(SITE_TUNING.qr.framePaddingPx),
        "--ziar-qr-frame-radius": px(SITE_TUNING.qr.frameRadiusPx),
        "--ziar-qr-compact-frame-padding": px(SITE_TUNING.qr.compactFramePaddingPx),
        "--ziar-qr-compact-frame-padding-small": px(SITE_TUNING.qr.compactFramePaddingSmallPx),
        "--ziar-qr-host-mobile-frame-width": px(SITE_TUNING.qr.hostMobileFrameWidthPx),
        "--ziar-qr-host-mobile-frame-width-small": px(SITE_TUNING.qr.hostMobileFrameWidthSmallPx),
        "--ziar-qr-host-mobile-frame-radius": px(SITE_TUNING.qr.hostMobileFrameRadiusPx),
        "--ziar-qr-host-mobile-frame-radius-small": px(SITE_TUNING.qr.hostMobileFrameRadiusSmallPx),
        "--ziar-qr-roster-overlay-frame-width": px(SITE_TUNING.qr.rosterOverlayFrameWidthPx),
        "--ziar-qr-roster-overlay-frame-width-small": px(SITE_TUNING.qr.rosterOverlayFrameWidthSmallPx),
        "--ziar-qr-roster-overlay-frame-padding": px(SITE_TUNING.qr.rosterOverlayFramePaddingPx),
        "--ziar-qr-roster-overlay-frame-padding-small": px(SITE_TUNING.qr.rosterOverlayFramePaddingSmallPx),
        "--ziar-qr-roster-overlay-frame-radius": px(SITE_TUNING.qr.rosterOverlayFrameRadiusPx),
        "--ziar-qr-roster-overlay-frame-radius-small": px(SITE_TUNING.qr.rosterOverlayFrameRadiusSmallPx),
        "--ziar-qr-roster-overlay-opacity": String(SITE_TUNING.qr.rosterOverlayOpacity),
        "--ziar-qr-active-room-frame-width": px(SITE_TUNING.qr.activeRoomFrameWidthPx),
        "--ziar-qr-active-room-frame-width-mobile": px(SITE_TUNING.qr.activeRoomFrameWidthMobilePx),
        "--ziar-qr-active-room-frame-width-small": px(SITE_TUNING.qr.activeRoomFrameWidthSmallPx),
        "--ziar-qr-active-room-host-frame-width": px(SITE_TUNING.qr.activeRoomHostFrameWidthPx),
        "--ziar-qr-active-room-host-frame-width-mobile": px(SITE_TUNING.qr.activeRoomHostFrameWidthMobilePx),
        "--ziar-qr-active-room-host-frame-width-small": px(SITE_TUNING.qr.activeRoomHostFrameWidthSmallPx),
        "--ziar-qr-active-room-host-compact-frame-width": px(SITE_TUNING.qr.activeRoomHostCompactFrameWidthPx),
        "--ziar-qr-active-room-host-short-frame-width": px(SITE_TUNING.qr.activeRoomHostShortFrameWidthPx),
        "--ziar-qr-active-room-host-stacked-frame-width": px(SITE_TUNING.qr.activeRoomHostStackedFrameWidthPx),
        "--ziar-qr-active-room-frame-radius": px(SITE_TUNING.qr.activeRoomFrameRadiusPx),
        "--ziar-qr-active-room-frame-radius-small": px(SITE_TUNING.qr.activeRoomFrameRadiusSmallPx),
        "--ziar-host-lobby-roster-max-width": px(SITE_TUNING.lobby.layout.hostDesktopRosterMaxWidthPx),
        "--ziar-host-lobby-qr-card-max-width": px(SITE_TUNING.lobby.layout.hostDesktopQrCardMaxWidthPx),
        "--ziar-host-lobby-roster-list-max-viewport-height": svh(
            SITE_TUNING.lobby.layout.hostRosterListMaxViewportHeightSvh
        ),
        "--ziar-host-lobby-roster-list-max-height": px(SITE_TUNING.lobby.layout.hostRosterListMaxHeightPx),
        "--ziar-host-lobby-panel-surface-mix": `${SITE_TUNING.lobby.layout.hostPanelSurfaceMixPercent}%`,
        "--ziar-host-lobby-panel-active-surface-mix": `${SITE_TUNING.lobby.layout.hostPanelActiveSurfaceMixPercent}%`,
        "--ziar-host-controller-qr-width": px(SITE_TUNING.qr.controllerInlineWidthPx),
        "--ziar-host-controller-qr-width-tablet": px(SITE_TUNING.qr.controllerInlineWidthTabletPx),
        "--ziar-host-controller-qr-width-mobile": px(SITE_TUNING.qr.controllerInlineWidthMobilePx),
        "--ziar-qr-controller-frame-radius": px(SITE_TUNING.qr.controllerFrameRadiusPx),
        "--ziar-qr-preview-panel-max-width": px(SITE_TUNING.qr.previewPanelMaxWidthPx),
        "--ziar-qr-preview-panel-max-width-mobile": px(SITE_TUNING.qr.previewPanelMaxWidthMobilePx),
        "--ziar-qr-preview-max-width": px(SITE_TUNING.qr.previewMaxWidthPx),
        "--ziar-qr-preview-max-width-mobile": px(SITE_TUNING.qr.previewMaxWidthMobilePx),
        "--ziar-qr-preview-max-height": svh(SITE_TUNING.qr.previewMaxHeightSvh),
        "--ziar-qr-preview-max-height-mobile": svh(SITE_TUNING.qr.previewMaxHeightMobileSvh),
        "--ziar-qr-preview-frame-radius": px(SITE_TUNING.qr.previewFrameRadiusPx),
        "--ziar-qr-preview-code-font-size": rem(SITE_TUNING.qr.previewCodeFontSizeRem),
        "--ziar-qr-preview-code-font-size-mobile": rem(SITE_TUNING.qr.previewCodeFontSizeMobileRem),
        "--ziar-host-mode-card-emblem-size": px(SITE_TUNING.hostMode.cardEmblemSizePx),
        "--ziar-host-mode-card-emblem-padding": px(SITE_TUNING.hostMode.cardEmblemPaddingPx),
        "--ziar-host-lobby-side-rail-min-width": px(SITE_TUNING.hostLobby.sideRailMinPx),
        "--ziar-host-lobby-side-rail-width": vw(SITE_TUNING.hostLobby.sideRailWidthVw),
        "--ziar-host-lobby-side-rail-max-width": px(SITE_TUNING.hostLobby.sideRailMaxPx),
        "--ziar-host-lobby-side-rail-max-share": `${SITE_TUNING.hostLobby.sideRailMaxSharePercent}%`,
        "--ziar-host-lobby-short-side-rail-min-width": px(SITE_TUNING.hostLobby.shortSideRailMinPx),
        "--ziar-host-lobby-short-side-rail-width": vw(SITE_TUNING.hostLobby.shortSideRailWidthVw),
        "--ziar-host-lobby-short-side-rail-max-width": px(SITE_TUNING.hostLobby.shortSideRailMaxPx),
        "--ziar-maze-cell-base": px(SITE_TUNING.relicRun.board.cellSizePx),
        "--ziar-maze-tile-size": px(SITE_TUNING.relicRun.board.tileSizePx),
        "--ziar-maze-overlay-treasure-size": px(SITE_TUNING.relicRun.board.overlayTreasureSizePx),
        "--ziar-maze-board-scale-mobile": String(SITE_TUNING.relicRun.board.mobileBoardScale),
        "--ziar-maze-board-scale-desktop": String(SITE_TUNING.relicRun.board.desktopBoardScale),
        "--ziar-maze-avatar-size-desktop": px(SITE_TUNING.relicRun.board.desktopAvatarSizePx),
        "--ziar-maze-avatar-radius-desktop": px(SITE_TUNING.relicRun.board.desktopAvatarRadiusPx),
        "--ziar-maze-avatar-border-desktop": px(SITE_TUNING.relicRun.board.desktopAvatarBorderPx),
        "--ziar-maze-avatar-halo-desktop": px(SITE_TUNING.relicRun.board.desktopAvatarHaloPx),
        "--ziar-maze-host-stage-card-min-height-mobile": svh(
            SITE_TUNING.relicRun.layout.mobileHostStageCardMinHeightSvh
        ),
        "--ziar-maze-host-stage-card-max-height-mobile": px(
            SITE_TUNING.relicRun.layout.mobileHostStageCardMaxHeightPx
        ),
        "--ziar-maze-host-stage-min-height-mobile": svh(SITE_TUNING.relicRun.layout.mobileHostStageMinHeightSvh),
        "--ziar-maze-host-stage-max-height-mobile": px(SITE_TUNING.relicRun.layout.mobileHostStageMaxHeightPx),
        "--ziar-maze-passage-preview-lines": String(SITE_TUNING.relicRun.passagePreview.maxLines),
        "--ziar-player-roster-roomy-column-min": px(SITE_TUNING.playerRoster.roomyColumnMinPx),
        "--ziar-player-roster-balanced-column-min": px(SITE_TUNING.playerRoster.balancedColumnMinPx),
        "--ziar-player-roster-compact-column-min": px(SITE_TUNING.playerRoster.compactColumnMinPx),
        "--ziar-player-roster-dense-column-min": px(SITE_TUNING.playerRoster.denseColumnMinPx),
        "--ziar-player-roster-roomy-row-min": px(SITE_TUNING.playerRoster.roomyRowMinPx),
        "--ziar-player-roster-balanced-row-min": px(SITE_TUNING.playerRoster.balancedRowMinPx),
        "--ziar-player-roster-compact-row-min": px(SITE_TUNING.playerRoster.compactRowMinPx),
        "--ziar-player-roster-dense-row-min": px(SITE_TUNING.playerRoster.denseRowMinPx),
        "--ziar-player-roster-roomy-gap": px(SITE_TUNING.playerRoster.roomyGapPx),
        "--ziar-player-roster-balanced-gap": px(SITE_TUNING.playerRoster.balancedGapPx),
        "--ziar-player-roster-compact-gap": px(SITE_TUNING.playerRoster.compactGapPx),
        "--ziar-player-roster-dense-gap": px(SITE_TUNING.playerRoster.denseGapPx),
        "--ziar-player-roster-roomy-row-padding-y": px(SITE_TUNING.playerRoster.roomyRowPaddingYPx),
        "--ziar-player-roster-balanced-row-padding-y": px(SITE_TUNING.playerRoster.balancedRowPaddingYPx),
        "--ziar-player-roster-compact-row-padding-y": px(SITE_TUNING.playerRoster.compactRowPaddingYPx),
        "--ziar-player-roster-dense-row-padding-y": px(SITE_TUNING.playerRoster.denseRowPaddingYPx),
        "--ziar-player-roster-roomy-row-padding-x": px(SITE_TUNING.playerRoster.roomyRowPaddingXPx),
        "--ziar-player-roster-balanced-row-padding-x": px(SITE_TUNING.playerRoster.balancedRowPaddingXPx),
        "--ziar-player-roster-compact-row-padding-x": px(SITE_TUNING.playerRoster.compactRowPaddingXPx),
        "--ziar-player-roster-dense-row-padding-x": px(SITE_TUNING.playerRoster.denseRowPaddingXPx),
        "--ziar-player-roster-roomy-action-size": px(SITE_TUNING.playerRoster.roomyActionSizePx),
        "--ziar-player-roster-balanced-action-size": px(SITE_TUNING.playerRoster.balancedActionSizePx),
        "--ziar-player-roster-compact-action-size": px(SITE_TUNING.playerRoster.compactActionSizePx),
        "--ziar-player-roster-dense-action-size": px(SITE_TUNING.playerRoster.denseActionSizePx),
        "--ziar-player-roster-roomy-badge-min-height": px(SITE_TUNING.playerRoster.roomyBadgeMinHeightPx),
        "--ziar-player-roster-balanced-badge-min-height": px(SITE_TUNING.playerRoster.balancedBadgeMinHeightPx),
        "--ziar-player-roster-compact-badge-min-height": px(SITE_TUNING.playerRoster.compactBadgeMinHeightPx),
        "--ziar-player-roster-dense-badge-min-height": px(SITE_TUNING.playerRoster.denseBadgeMinHeightPx),
        "--ziar-player-roster-roomy-name-font-size": rem(SITE_TUNING.playerRoster.roomyNameFontSizeRem),
        "--ziar-player-roster-single-roomy-name-font-size": rem(
            SITE_TUNING.playerRoster.singleRoomyNameFontSizeRem
        ),
        "--ziar-player-roster-balanced-name-font-size": rem(SITE_TUNING.playerRoster.balancedNameFontSizeRem),
        "--ziar-player-roster-compact-name-font-size": rem(SITE_TUNING.playerRoster.compactNameFontSizeRem),
        "--ziar-player-roster-dense-name-font-size": rem(SITE_TUNING.playerRoster.denseNameFontSizeRem),
        "--ziar-player-roster-roomy-badge-font-size": rem(SITE_TUNING.playerRoster.roomyBadgeFontSizeRem),
        "--ziar-player-roster-balanced-badge-font-size": rem(SITE_TUNING.playerRoster.balancedBadgeFontSizeRem),
        "--ziar-player-roster-compact-badge-font-size": rem(SITE_TUNING.playerRoster.compactBadgeFontSizeRem),
        "--ziar-player-roster-dense-badge-font-size": rem(SITE_TUNING.playerRoster.denseBadgeFontSizeRem),
        "--ziar-host-start-loading-reveal-ms": `${SITE_TUNING.room.hostStartLoadingOverlayRevealMs}ms`,
        "--ziar-player-roster-floating-count-size": px(SITE_TUNING.playerRoster.floatingCountSizePx),
        "--ziar-player-roster-floating-count-gutter": px(SITE_TUNING.playerRoster.floatingCountGutterPx),
        "--ziar-random-teams-stage-result-control-gutter": px(
            SITE_TUNING.randomTeams.layout.stageResultControlGutterPx
        ),
        "--ziar-random-teams-stage-order-result-control-gutter": px(
            SITE_TUNING.randomTeams.layout.stageOrderResultControlGutterPx
        ),
        "--ziar-vault-surface-settle-ms": `${SITE_TUNING.vaultBreakers.motion.surfaceSettleMs}ms`,
        "--ziar-vault-banknote-sheen-ms": `${SITE_TUNING.vaultBreakers.banknote.sheenMs}ms`,
        "--ziar-vault-banknote-max-width": px(SITE_TUNING.vaultBreakers.banknote.maxWidthPx),
        "--ziar-vault-surface-exit-ms": `${SITE_TUNING.vaultBreakers.motion.surfaceExitMs}ms`,
        "--ziar-vault-surface-exit-scale": String(SITE_TUNING.vaultBreakers.motion.surfaceExitScale),
        "--ziar-vault-surface-start-opacity": String(SITE_TUNING.vaultBreakers.motion.surfaceStartOpacity),
        "--ziar-vault-surface-start-scale": String(SITE_TUNING.vaultBreakers.motion.surfaceStartScale),
        "--ziar-vault-entry-leap-ms": `${SITE_TUNING.vaultBreakers.motion.vaultEntryLeapMs}ms`,
        "--ziar-vault-entry-zoom-ms": `${SITE_TUNING.vaultBreakers.motion.vaultEntryZoomMs}ms`,
        "--ziar-vault-entry-zoom-scale": String(SITE_TUNING.vaultBreakers.motion.vaultEntryZoomScale),
        "--ziar-vault-return-zoom-ms": `${SITE_TUNING.vaultBreakers.motion.vaultReturnZoomMs}ms`,
        "--ziar-vault-return-fade-ms": `${SITE_TUNING.vaultBreakers.motion.vaultReturnFadeMs}ms`,
        "--ziar-vault-surface-easing": SITE_TUNING.vaultBreakers.motion.surfaceEasing,
        "--ziar-vault-crack-handoff-ms": `${SITE_TUNING.vaultBreakers.motion.crackHandoffMs}ms`,
        "--ziar-vault-stash-increase-ms": `${SITE_TUNING.vaultBreakers.motion.stashIncreaseMs}ms`,
        "--ziar-vault-final-pulse-ms": `${SITE_TUNING.vaultBreakers.motion.finalVaultPulseMs}ms`,
        "--ziar-vault-first-roll-glow-ms": `${SITE_TUNING.vaultBreakers.motion.firstRollGlowMs}ms`,
        "--ziar-vault-scanner-discovery-pulse-ms": `${SITE_TUNING.vaultBreakers.motion.scannerDiscoveryPulseMs}ms`,
        "--ziar-vault-roster-name-min-font-size": rem(SITE_TUNING.vaultBreakers.roster.nameMinFontSizeRem),
        "--ziar-vault-category-min-font-size": px(SITE_TUNING.vaultBreakers.typography.categoryMinPx),
        "--ziar-vault-category-max-font-size": px(SITE_TUNING.vaultBreakers.typography.categoryMaxPx),
        "--ziar-vault-scanner-ready-pulse-ms": `${SITE_TUNING.vaultBreakers.motion.scannerReadyPulseMs}ms`,
        "--ziar-vault-scanner-trace-loop-ms": `${SITE_TUNING.vaultBreakers.motion.scannerTraceLoopMs}ms`,
        "--ziar-vault-player-capy-size": px(SITE_TUNING.vaultBreakers.mascot.playerWaitingSizePx),
        "--ziar-vault-player-capy-size-mobile": px(SITE_TUNING.vaultBreakers.mascot.playerWaitingCompactSizePx),
        "--ziar-vault-player-capy-idle-ms": `${SITE_TUNING.vaultBreakers.mascot.playerWaitingIdleMs}ms`
    };

    Object.entries(variables).forEach(([name, value]) => {
        root.style.setProperty(name, value);
    });

    root.classList.toggle("ziar-vault-motion-enabled", vaultMotionEnabled);
    root.classList.toggle("ziar-vault-motion-disabled", !vaultMotionEnabled);
};
